/* eslint-disable */
import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Row } from '@thd-olt-component-react/core-ui';
import { QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { LayoutRenderer } from '@thd-olt-component-react/layout-renderer';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { Metadata,
  HomepageMetadata,
  SiteSearchMetadata,
} from '@thd-olt-component-react/metadata';
import { LayoutGroup } from '@thd-olt-component-react/layout-manager';

import { componentOptions } from './component-options';
import {
  CUSTOMER_TYPES,
  userAuthorizationStatus,
  getSvocId,
  getUserId,
  getMarketingCloudVisitorID,
  getGuestUserId,
} from '../../utils';
import {
  guestB2cDataModel,
  homepagePreviewByIDDataModel,
} from '../../datamodels/home';
import { createComponents } from '../../components/components';
import { usePreviewByID, useWindowWidth } from '../../hooks';
import '../../styles/home.scss';

const title = 'The Home Depot';

const staticMetadata = {
  title,
  description:
    'Shop online for all your home improvement needs: appliances, bathroom decorating ideas, kitchen remodeling, patio furniture, power tools, bbq grills, carpeting, lumber, concrete, lighting, ceiling fans and more at The Home Depot.', // eslint-disable-line
  facebookAdmins: '100005416960152',
  siteName: title,
  type: 'homepage',
  canonicalURL: '/',
  robots: 'noodp',
};

function renderMetadata(metadata) {
  return (
    <LayoutGroup name="metadata">
      <QueryProvider cacheKey="homepage-metadata">
        <Metadata data={{ ...staticMetadata, ...metadata }}>
          <HomepageMetadata>
            <SiteSearchMetadata />
          </HomepageMetadata>
        </Metadata>
      </QueryProvider>
    </LayoutGroup>
  );
}

export const GuestHomepage = ({ slug }) => {
  declareContexts([QueryContext, ExperienceContext]);
  const { channel, customer } = useContext(ExperienceContext);
  const store = useStore();
  const windowWidthMeasure = useWindowWidth();
  const userState = {
    userId: getSvocId() || getUserId(),
    mcvisID: getMarketingCloudVisitorID(),
    userAuthStatus: userAuthorizationStatus(),
    guestUserId: getGuestUserId(),
  };

  const { guestUserId, mcvisID, userAuthStatus } = userState;
  const contentfulPreviewId = usePreviewByID();
  let layoutDataModel = guestB2cDataModel;
  /**
   * This is not a class in the CSS sense. It's referring
   * to the type of layout we have in Contentful, which
   * at the time of writing seems to be a choice between
   * PersonalizedHomepage and EducationalLayout.
   */
  let layoutClass;

  if (contentfulPreviewId) {
    layoutDataModel = homepagePreviewByIDDataModel;
    layoutClass = 'PersonalizedHomePage';
  }

  const components = createComponents({
    customerType: CUSTOMER_TYPES.GUEST,
    guestUserId,
    mcvisID,
    store,
    userAuthStatus,
    channel,
    customer,
    windowWidthMeasure,
  });

  useEffect(function triggerHomepageLoadEvent() {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
      'guest-homepage.page-load',
      'homepage'
    );
  }, []);

  useEffect(function setThdOnlineChannelCookie() {
    if (channel === 'mobile') {
      const encodedValue = encodeURIComponent('E1=S2');
      window.cookieUtils.fed_CreateCookie('THD_ONLINE_CHANNEL', encodedValue, 1);
    }
  }, []);

  return (
    <Row className="isBound" style={{ paddingBottom: '3rem' }}>
      <QueryProvider cacheKey="php-layout">
        <LayoutRenderer
          components={components}
          componentOptions={componentOptions}
          slug={slug}
          contentfulPreviewId={contentfulPreviewId}
          layoutClass={layoutClass}
          layoutDataModel={layoutDataModel}
          onContentInformationApiError={() => {
            if (typeof window !== 'undefined') {
              window.location.replace('/c/site_map');
            }
          }}
          ssr
          renderMetadata={renderMetadata}
        />
      </QueryProvider>
    </Row>
  );
};

GuestHomepage.propTypes = {
  slug: PropTypes.string,
};
GuestHomepage.defaultProps = {
  slug: undefined,
};

GuestHomepage.displayName = 'GuestHomepage';
